import * as React from "react";
import { Cell, Pie, PieChart } from "recharts";

import { cn } from "../../utils/utils";
import {
    ChartConfig,
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
} from "../ui/chart";

export interface DonutDatum {
    /** Chave da fatia (deve existir no `config`). */
    key: string;
    /** Valor absoluto — o donut calcula a proporção. */
    value: number;
}

interface DonutChartProps {
    data: DonutDatum[];
    /** Rótulos + cores das fatias. A cor vira `--color-<key>`. */
    config: ChartConfig;
    /** Conteúdo central (ex.: total). */
    centerLabel?: React.ReactNode;
    centerSub?: React.ReactNode;
    className?: string;
}

/**
 * Donut "fatia do todo" padronizado sobre `ui/chart`: cada categoria é uma
 * fração proporcional, com furo central para um headline (total/dominante) e
 * legenda com o percentual de cada fatia. Cores temáveis via `config`.
 */
export function DonutChart({
    data,
    config,
    centerLabel,
    centerSub,
    className,
}: DonutChartProps) {
    const total = data.reduce((sum, d) => sum + (d.value || 0), 0);
    const pct = (v: number) => (total ? Math.round((v / total) * 100) : 0);
    const chartData = data.map((d) => ({
        ...d,
        fill: `var(--color-${d.key})`,
    }));

    return (
        <div className={cn("flex h-full w-full flex-col", className)}>
            <div className="relative flex-1">
                <ChartContainer
                    config={config}
                    className="aspect-auto h-full w-full [&_.recharts-sector]:[filter:drop-shadow(0_1px_3px_hsl(var(--foreground)/0.12))]"
                >
                    <PieChart>
                        <ChartTooltip
                            cursor={false}
                            content={
                                <ChartTooltipContent
                                    nameKey="key"
                                    formatter={(value, name) => (
                                        <div className="flex w-full items-center justify-between gap-3">
                                            <span className="text-muted-foreground">
                                                {config[
                                                    name as keyof typeof config
                                                ]?.label ?? name}
                                            </span>
                                            <span className="font-mono font-medium tabular-nums text-foreground">
                                                {pct(Number(value))}%
                                            </span>
                                        </div>
                                    )}
                                />
                            }
                        />
                        <Pie
                            data={chartData}
                            dataKey="value"
                            nameKey="key"
                            innerRadius="58%"
                            outerRadius="100%"
                            paddingAngle={2}
                            cornerRadius={6}
                            strokeWidth={0}
                        >
                            {chartData.map((d) => (
                                <Cell key={d.key} fill={d.fill} />
                            ))}
                        </Pie>
                    </PieChart>
                </ChartContainer>

                {(centerLabel || centerSub) && (
                    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
                        {centerLabel && (
                            <span className="text-3xl font-bold leading-none text-foreground">
                                {centerLabel}
                            </span>
                        )}
                        {centerSub && (
                            <span className="mt-1 max-w-[60%] text-xs text-muted-foreground">
                                {centerSub}
                            </span>
                        )}
                    </div>
                )}
            </div>

            <ul className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5">
                {data.map((d) => (
                    <li
                        key={d.key}
                        className="flex items-center gap-1.5 text-xs text-muted-foreground"
                    >
                        <span
                            className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
                            style={{ backgroundColor: `var(--color-${d.key})` }}
                        />
                        <span>{config[d.key]?.label ?? d.key}</span>
                        <span className="font-semibold text-foreground tabular-nums">
                            {pct(d.value)}%
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}
